import { useState, useEffect, useCallback, useRef } from "react";
import { createContext, useContext } from "react";

// Create context outside the hook
const WishlistContext = createContext(null);

export const useWishlist = () => {
  const [wishlist, setWishlist] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasLoaded = useRef(false);

  // Load wishlist from localStorage on mount
  useEffect(() => {
    if (hasLoaded.current) return;
    hasLoaded.current = true;

    const loadWishlist = () => {
      try {
        const savedWishlist = localStorage.getItem("wishlist");
        if (savedWishlist) {
          const parsedWishlist = JSON.parse(savedWishlist);
          // Validate wishlist structure
          if (Array.isArray(parsedWishlist)) {
            // Filter out invalid items
            const validWishlist = parsedWishlist.filter(
              (item) => item && item.id && item.name,
            );
            setWishlist(validWishlist);
          } else {
            setWishlist([]);
          }
        } else {
          setWishlist([]);
        }
      } catch (err) {
        setError("Failed to load wishlist");
        setWishlist([]);
      }
    };

    loadWishlist();
  }, []);

  // Save wishlist to localStorage whenever it changes
  useEffect(() => {
    // Skip saving if wishlist is empty on initial load
    if (wishlist.length === 0 && !hasLoaded.current) return;

    try {
      localStorage.setItem("wishlist", JSON.stringify(wishlist));
    } catch (err) {
      setError("Failed to save wishlist");
    }
  }, [wishlist]);

  // Add a backup mechanism
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Backup wishlist before page unload
      localStorage.setItem("wishlist_backup", JSON.stringify(wishlist));
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [wishlist]);

  const addToWishlist = useCallback(async (product) => {
    setIsLoading(true);
    setError(null);

    try {
      // Validate product
      if (!product || !product.id) {
        throw new Error("Invalid product: missing ID");
      }

      setWishlist((prevWishlist) => {
        // Check if product already exists in wishlist
        const exists = prevWishlist.some((item) => item.id === product.id);

        if (exists) {
          // Update existing item with new data
          return prevWishlist.map((item) =>
            item.id === product.id
              ? {
                  ...item,
                  ...product,
                  updatedAt: Date.now(),
                }
              : item,
          );
        }

        // Add new item to wishlist
        const newItem = {
          id: product.id,
          name: product.name || "Unknown Product",
          price: typeof product.price === "number" ? product.price : 0,
          originalPrice: product.originalPrice || product.price || 0,
          image: product.image || "",
          category: product.category || "",
          sku: product.sku || "",
          addedAt: Date.now(),
          updatedAt: Date.now(),
          // Include additional fields
          ...(product.isSale && { isSale: product.isSale }),
          ...(product.discountPercentage && {
            discountPercentage: product.discountPercentage,
          }),
          ...(product.isNew && { isNew: product.isNew }),
          ...(product.isFeatured && { isFeatured: product.isFeatured }),
        };

        return [...prevWishlist, newItem];
      });

      return true;
    } catch (err) {
      setError(err.message || "Failed to add item to wishlist");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const removeFromWishlist = useCallback(async (productId) => {
    setIsLoading(true);
    setError(null);

    try {
      setWishlist((prevWishlist) =>
        prevWishlist.filter((item) => item.id !== productId),
      );

      return true;
    } catch (err) {
      setError(err.message || "Failed to remove item from wishlist");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearWishlist = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      setWishlist([]);
      return true;
    } catch (err) {
      setError(err.message || "Failed to clear wishlist");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const moveToCart = useCallback(
    async (productId, useCartHook) => {
      setIsLoading(true);
      setError(null);

      try {
        const product = wishlist.find((item) => item.id === productId);
        if (!product) {
          throw new Error("Product not found in wishlist");
        }

        // Add to cart using provided cart hook
        const cartProduct = {
          ...product,
          quantity: 1,
        };

        const success = await useCartHook.addToCart(cartProduct);
        if (success) {
          await removeFromWishlist(productId);
          return true;
        }

        return false;
      } catch (err) {
        setError(err.message || "Failed to move item to cart");
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [wishlist, removeFromWishlist],
  );

  const isInWishlist = useCallback(
    (productId) => {
      return wishlist.some((item) => item.id === productId);
    },
    [wishlist],
  );

  const getWishlistItem = useCallback(
    (productId) => {
      return wishlist.find((item) => item.id === productId);
    },
    [wishlist],
  );

  const toggleWishlist = useCallback(
    async (product) => {
      if (!product || !product.id) {
        setError("Invalid product");
        return false;
      }

      if (isInWishlist(product.id)) {
        return await removeFromWishlist(product.id);
      } else {
        return await addToWishlist(product);
      }
    },
    [isInWishlist, addToWishlist, removeFromWishlist],
  );

  const exportWishlist = useCallback(() => {
    try {
      const dataStr = JSON.stringify(wishlist, null, 2);
      const dataBlob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(dataBlob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `wishlist-${Date.now()}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      return true;
    } catch (err) {
      setError("Failed to export wishlist");
      return false;
    }
  }, [wishlist]);

  const importWishlist = useCallback(async (file) => {
    setIsLoading(true);
    setError(null);

    try {
      const text = await file.text();
      const importedWishlist = JSON.parse(text);

      if (!Array.isArray(importedWishlist)) {
        throw new Error("Invalid wishlist format");
      }

      // Validate imported items
      const validWishlist = importedWishlist.filter(
        (item) => item && item.id && item.name,
      );

      // Merge with existing wishlist (avoid duplicates)
      setWishlist((prev) => {
        const existingIds = new Set(prev.map((item) => item.id));
        const newItems = validWishlist.filter(
          (item) => !existingIds.has(item.id),
        );
        return [...prev, ...newItems];
      });

      return validWishlist;
    } catch (err) {
      setError(err.message || "Failed to import wishlist");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getWishlistStats = useCallback(() => {
    const totalItems = wishlist.length;
    const totalValue = wishlist.reduce(
      (sum, item) => sum + (item.price || 0),
      0,
    );
    const originalTotalValue = wishlist.reduce(
      (sum, item) => sum + (item.originalPrice || item.price || 0),
      0,
    );
    const categories = [
      ...new Set(wishlist.map((item) => item.category).filter(Boolean)),
    ];

    const savings = wishlist.reduce((sum, item) => {
      const original = item.originalPrice || item.price || 0;
      const current = item.price || 0;
      return sum + Math.max(0, original - current);
    }, 0);

    return {
      totalItems,
      totalValue,
      originalTotalValue,
      savings,
      categories,
      averagePrice: totalItems > 0 ? totalValue / totalItems : 0,
      onSaleCount: wishlist.filter((item) => item.isSale).length,
      newItemsCount: wishlist.filter((item) => item.isNew).length,
    };
  }, [wishlist]);

  // Sync wishlist with server
  const syncWishlist = useCallback(async () => {
    setIsLoading(true);
    try {
      // TODO: Implement server sync
      return true;
    } catch (err) {
      setError("Failed to sync wishlist");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    // State
    wishlist,
    isLoading,
    error,

    // Wishlist Info
    wishlistCount: wishlist.length,
    isEmpty: wishlist.length === 0,
    stats: getWishlistStats(),

    // Methods
    addToWishlist,
    removeFromWishlist,
    clearWishlist,
    moveToCart,
    toggleWishlist,
    exportWishlist,
    importWishlist,
    syncWishlist,

    // Checkers
    isInWishlist,
    getWishlistItem,

    // Helper
    setError,
  };
};

// Wishlist Context Provider
export const WishlistProvider = ({ children }) => {
  const wishlist = useWishlist();

  return (
    <WishlistContext.Provider value={wishlist}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlistContext = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error(
      "useWishlistContext must be used within a WishlistProvider",
    );
  }
  return context;
};
